import type { BrandSpecials } from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import type { CardCarouselProps } from '@iheartjane/menu';
import { CardCarousel } from '@iheartjane/menu';
import type { Nullable } from '@iheartjane/types';
import { pluralize } from '@iheartjane/utils';
import { clsx } from 'clsx';
import { useEffect } from 'react';

import { ProductCard } from '@jane/bloom/components/productCard/productCard';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { Cart } from '@jane/bloom/types/schemas/bloom/checkout';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { StoreSpecials } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import { buildSingleProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearchResult';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';
import type { AlgoliaProduct } from '@jane/search/types';

import styles from './menuRow.module.css';

export type DMMenuRowProps = {
  brandSpecials: BrandSpecials;
  cart?: Nullable<Cart>;
  customLanguage: CustomLanguage;
  embedInProductGrid?: boolean;
  janeGold: JaneGold;
  menuTopRow: SmartSort<AlgoliaProduct>;
  mode?: CardCarouselProps['mode'];
  pauseRefetching: () => void;
  pinnedStore?: Nullable<StoreViewModel>;
  productSelected?: (product: ProductSearchResultViewModel) => void;
  specials: Nullable<StoreSpecials>;
  unpauseRefetching: () => void;
};

export function DMMenuRow({
  brandSpecials,
  cart,
  customLanguage,
  embedInProductGrid,
  janeGold,
  menuTopRow,
  mode = 'ideal-width',
  pauseRefetching,
  pinnedStore,
  productSelected,
  specials,
  unpauseRefetching,
}: DMMenuRowProps) {
  const { featureFlags } = useRootLoaderData();
  const { products, title } = menuTopRow;
  const visibleProductCount = products.length;

  useEffect(() => {
    if (menuTopRow) {
      menuTopRow.onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCardSelect = async (product: ProductSearchResultViewModel) => {
    productSelected?.(product);
  };

  useEffect(() => {
    products.forEach((product) => product.impress());
  }, [products]);

  if (!products.length) {
    return null;
  }

  return (
    <CardCarousel
      cardWidth={240}
      cardWidthMobile={162}
      className={clsx({
        customRow: !embedInProductGrid,
        [styles['topMenuRow']]: embedInProductGrid,
      })}
      data-testid="sponsored-row"
      mode={mode}
      name={title ?? ''}
      onMouseEnter={pauseRefetching}
      onMouseLeave={unpauseRefetching}
      subtitle={
        embedInProductGrid
          ? undefined
          : pluralize({ noun: 'product', number: visibleProductCount })
      }
      surtitle="Sponsored"
    >
      {products.map((product, ind) => {
        const builtProduct = buildSingleProductSearchResultViewModel({
          bundleTag: featureFlags[FLAGS.c1BundleTag],
          cart,
          customLanguage,
          janeGold,
          pinnedStore,
          product: product.attributes,
          specials,
        });
        return (
          <CardCarousel.Card key={product.objectId}>
            <ProductCard
              brandSpecials={brandSpecials}
              product={builtProduct}
              selected={() => {
                product.click();
                onCardSelect(builtProduct);
              }}
              preloaded={ind <= 5}
              smartProductInstance={product}
            />
          </CardCarousel.Card>
        );
      })}
    </CardCarousel>
  );
}
